import { useEffect, useMemo, useState } from 'react';
import { useAuth, useLocale } from '../../context';
import { api } from '../../api';
import { accountSectionFromPath } from '../../data/accountData';
import {
  IconAccountBets,
  IconAccountBonuses,
  IconAccountCashier,
  IconAccountProfile,
  IconAccountPromos,
  IconAccountSecurity,
  IconAccountSupport,
  IconBellFilled,
  IconInAppGift,
} from '../VurgunIcons';
import { pushRoute } from '../../navigation';

const TR_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

function formatNotificationDate(value, locale) {
  if (!value) return '';
  const date = new Date(value);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  if (locale === 'tr') {
    return `${date.getDate()}\u00a0${TR_MONTHS[date.getMonth()]},\u00a0${hours}:${minutes}`;
  }

  const month = date.toLocaleString('en-GB', { month: 'long' });
  return `${date.getDate()}\u00a0${month},\u00a0${hours}:${minutes}`;
}

function notificationLink(notification) {
  return (
    notification?.action_url
    || notification?.href
    || notification?.link
    || notification?.path
    || ''
  ).trim();
}

function notificationKind(notification) {
  const type = String(notification?.type || notification?.category || '').toLowerCase();
  const text = `${notification?.title || ''} ${notification?.body || ''}`.toLowerCase();

  if (['promo', 'promotion', 'promos', 'action', 'campaign'].includes(type) || /promo|kampanya|turnuva|tournament/.test(text)) {
    return 'promo';
  }
  if (['bonus', 'bonuses', 'freespin', 'free_spin', 'fs', 'gift'].includes(type) || /\b(fs|free\s*spin|freespin|bonus)\b/.test(text)) {
    return 'bonus';
  }
  if (['withdraw', 'withdrawal', 'çekim'].includes(type) || /para çek|çekim|withdraw/.test(text)) {
    return 'withdraw';
  }
  if (['deposit', 'cashier', 'payment', 'payments', 'finance'].includes(type) || /para yatır|yatırım|deposit|ödeme/.test(text)) {
    return 'deposit';
  }
  if (['support', 'ticket', 'help'].includes(type) || /destek|talebiniz|yanıt/.test(text)) {
    return 'support';
  }
  if (['security', 'responsible', 'limit'].includes(type) || /güvenlik|sorumlu|limit/.test(text)) {
    return 'security';
  }
  if (['kyc', 'verification', 'verify', 'profile', 'welcome', 'account'].includes(type) || /doğrula|hoş geld|profil|üyeli/.test(text)) {
    return 'profile';
  }
  if (['bets', 'bet', 'history', 'sports', 'sport'].includes(type) || /bahis|spor/.test(text)) {
    return 'bets';
  }
  if (['casino', 'slots', 'slot'].includes(type) || /casino|slot/.test(text)) {
    return 'casino';
  }
  if (['system', 'info', 'news'].includes(type)) {
    return 'system';
  }
  return type || 'system';
}

function iconForKind(kind) {
  switch (kind) {
    case 'promo':
      return { Icon: IconAccountPromos, tone: 'promo' };
    case 'bonus':
      return { Icon: IconInAppGift, tone: 'bonus' };
    case 'deposit':
    case 'withdraw':
    case 'cashier':
      return { Icon: IconAccountCashier, tone: 'cashier' };
    case 'support':
      return { Icon: IconAccountSupport, tone: 'support' };
    case 'security':
      return { Icon: IconAccountSecurity, tone: 'security' };
    case 'profile':
    case 'welcome':
    case 'kyc':
      return { Icon: IconAccountProfile, tone: 'profile' };
    case 'bets':
    case 'sport':
    case 'sports':
      return { Icon: IconAccountBets, tone: 'bets' };
    case 'casino':
      return { Icon: IconAccountBonuses, tone: 'casino' };
    default:
      return { Icon: IconBellFilled, tone: 'system' };
  }
}

function sheetViewForAccountSection(section, path = '') {
  const lower = String(path).toLowerCase();
  if (section === 'bonuses') return { kind: 'route', page: 'vip' };
  if (section === 'verification') return { kind: 'sheet', view: 'profile' };
  if (section === 'overview' || !section) return { kind: 'sheet', view: 'profile' };
  if (section === 'deposit' || section === 'withdraw' || section === 'withdrawal') return { kind: 'sheet', view: 'cashier' };
  if (section === 'cashier') {
    const tab = /withdraw|çek/.test(lower) ? 'withdraw' : 'deposit';
    return { kind: 'sheet', view: 'cashier', options: { tab } };
  }
  if (['profile', 'bets', 'notifications', 'security', 'support'].includes(section)) {
    return { kind: 'sheet', view: section };
  }
  return { kind: 'sheet', view: 'profile' };
}

function targetFromPath(rawPath) {
  let path = rawPath;
  try {
    if (/^https?:\/\//i.test(rawPath)) {
      const url = new URL(rawPath);
      if (typeof window !== 'undefined' && url.origin === window.location.origin) {
        path = `${url.pathname}${url.search}`;
      } else {
        return { kind: 'external', url: rawPath };
      }
    }
  } catch {
    return { kind: 'external', url: rawPath };
  }

  if (!path.startsWith('/')) path = `/${path}`;

  if (path.startsWith('/account')) {
    return sheetViewForAccountSection(accountSectionFromPath(path), path);
  }
  if (path.startsWith('/promos')) {
    let promosKind = 'actions';
    if (path.startsWith('/promos/tournaments')) promosKind = 'tournaments';
    else if (path.startsWith('/promos/bonuses')) promosKind = 'bonuses';
    else if (path.startsWith('/promos/promo-code')) promosKind = 'promoCode';
    return { kind: 'route', page: 'promos', opts: { promosKind } };
  }
  if (path.startsWith('/promo-vip') || path.startsWith('/vip')) {
    return { kind: 'route', page: 'vip' };
  }
  if (path.startsWith('/casino')) {
    return { kind: 'route', page: 'casino' };
  }
  if (path.startsWith('/sport')) {
    return { kind: 'route', page: 'sport' };
  }
  if (path.startsWith('/help')) {
    return { kind: 'route', page: 'help' };
  }
  return { kind: 'href', url: path };
}

function targetFromType(notification) {
  const kind = notificationKind(notification);
  switch (kind) {
    case 'promo':
      return { kind: 'route', page: 'promos' };
    case 'bonus':
      return { kind: 'route', page: 'vip' };
    case 'deposit':
    case 'cashier':
      return { kind: 'sheet', view: 'cashier', options: { tab: 'deposit' } };
    case 'withdraw':
      return { kind: 'sheet', view: 'cashier', options: { tab: 'withdraw' } };
    case 'support':
      return { kind: 'sheet', view: 'support' };
    case 'security':
      return { kind: 'sheet', view: 'security' };
    case 'profile':
    case 'welcome':
    case 'kyc':
      return { kind: 'sheet', view: 'profile' };
    case 'bets':
    case 'sport':
    case 'sports':
      return { kind: 'sheet', view: 'bets' };
    case 'casino':
      return { kind: 'route', page: 'casino' };
    default:
      return { kind: 'sheet', view: 'profile' };
  }
}

function resolveNotificationTarget(notification) {
  const link = notificationLink(notification);
  if (link) return targetFromPath(link);
  return targetFromType(notification);
}

export default function MobileAccountNotifications({ onRead, onNavigate, onOpenSheetView }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = () => {
    if (!token) return;
    setLoading(true);
    api.notifications(token)
      .then((list) => setItems(Array.isArray(list) ? list : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    (async () => {
      try {
        await api.readAllNotifications(token);
        if (alive) onRead?.();
      } catch {
        /* ignore */
      }
      if (alive) load();
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const visibleItems = useMemo(() => {
    if (filter === 'unread') return items.filter((n) => !n.read);
    return items;
  }, [filter, items]);

  const markRead = async (id) => {
    await api.readNotification(token, id);
    load();
    onRead?.();
  };

  const goToTarget = (target) => {
    if (!target) return;

    if (target.kind === 'external') {
      window.open(target.url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (target.kind === 'href') {
      window.location.href = target.url;
      return;
    }

    if (target.kind === 'sheet') {
      if (onOpenSheetView) {
        onOpenSheetView(target.view, target.options || {});
        return;
      }
      onNavigate?.('account', { accountSection: target.view === 'cashier' ? 'cashier' : target.view });
      pushRoute('account', { accountSection: target.view === 'cashier' ? 'cashier' : target.view });
      return;
    }

    if (target.kind === 'route') {
      onNavigate?.(target.page, target.opts);
      pushRoute(target.page, target.opts);
    }
  };

  const handleOpen = async (notification) => {
    if (!notification.read) await markRead(notification.id);
    goToTarget(resolveNotificationTarget(notification));
  };

  return (
    <div className="mobile-account-notifications">
      <div className="mobile-account-notifications__tips" role="tablist" aria-label={tr('account.nav.notifications')}>
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'all'}
          className={`mobile-account-notifications__tip${filter === 'all' ? ' is-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          {tr('account.notifications.filterAll')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'unread'}
          className={`mobile-account-notifications__tip${filter === 'unread' ? ' is-active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          {tr('account.notifications.filterUnread', { count: unreadCount })}
        </button>
      </div>

      {loading ? (
        <div className="mobile-account-notifications__loading">{tr('account.loading')}</div>
      ) : visibleItems.length === 0 ? (
        <div className="mobile-account-notifications__empty">
          {filter === 'unread' && unreadCount === 0 && items.length > 0
            ? tr('account.notifications.emptyUnread')
            : tr('account.notifications.empty')}
        </div>
      ) : (
        <div className="mobile-account-notifications__list">
          {visibleItems.map((n, index) => {
            const kind = notificationKind(n);
            const { Icon, tone } = iconForKind(kind);
            return (
              <article
                key={n.id}
                className={`mobile-account-notifications__card${n.read ? '' : ' is-unread'}${index === visibleItems.length - 1 ? ' is-last' : ''}`}
                onClick={() => handleOpen(n)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpen(n);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span
                  className={`mobile-account-notifications__icon mobile-account-notifications__icon--${tone}`}
                  aria-hidden="true"
                >
                  <Icon />
                </span>
                <div className="mobile-account-notifications__content">
                  <div className="mobile-account-notifications__title">{n.title}</div>
                  {n.body ? <p className="mobile-account-notifications__body">{n.body}</p> : null}
                  {n.image_url ? (
                    <img
                      className="mobile-account-notifications__image"
                      src={n.image_url}
                      alt=""
                    />
                  ) : null}
                  <div className="mobile-account-notifications__meta">
                    <button
                      type="button"
                      className="mobile-account-notifications__action"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpen(n);
                      }}
                    >
                      {n.action_label || tr('account.notifications.details')}
                    </button>
                    <span className="mobile-account-notifications__date">
                      {formatNotificationDate(n.created_at, locale)}
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
